import { useEffect } from 'react';
import { AuthActionType, useGetMe } from '@linkie/shared';
import { resetPlaygroundSession } from '@/modules/playground/utils/reset-playground-session';
import { isLoggedIn } from '@/core/utils/is-logged-in';
import useAuth from './useAuth.hook';

const useSyncAuthIdentity = () => {
  const { state, dispatch } = useAuth();

  // `state.isAuthenticated` only turns true once the store rehydrates out of IndexedDB or
  // a login dispatches into it, so gating on it alone made this query unable to do the one
  // thing it exists for: recover the identity when that rehydration gives us nothing. The
  // session itself lives in the `user_access_token` cookie — the same thing ProtectedRoute
  // and the API trust — so ask whenever the cookie is there and let a 401 below decide.
  const { data, error } = useGetMe({
    queryConfig: {
      enabled: state.isAuthenticated || isLoggedIn(),
      retry: false,
    },
  });

  const sessionUser = data?.data;
  const persistedUserId = state.user?._id;

  useEffect(() => {
    if (!sessionUser?._id) return;
    if (sessionUser._id === persistedUserId) return;

    if (persistedUserId) resetPlaygroundSession();

    dispatch({ type: AuthActionType.SET_USER, payload: sessionUser });
  }, [sessionUser, persistedUserId, dispatch]);

  useEffect(() => {
    const status = (error as { response?: { status?: number } } | null)?.response?.status;
    if (status !== 401) return;

    dispatch({ type: AuthActionType.LOGOUT_USER });
  }, [error, dispatch]);
};

export default useSyncAuthIdentity;
